import Images from "assets/images"
import {
    COMPLIANCE,
    ASC,
} from "const"
import {
    CcLibraryItemList,
    CcLibrarySet,
    MtProject,
} from "server/models"
import {
    ModuleList,
    UserList,
    UserSettings,
    UserData,
    LangType,
    CcLibrarySetList,
    ComplianceRevisionActiveList,
    ComplianceStateList,
    ComplianceHistoryList,

    GetFile,

    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadAllCcLibrarySets,
    LoadNestedItems,
    LoadCcComplianceRevisions,
    LoadAllCcComplianceItemState,
    LoadAllCcComplianceHistory,
    SortCcLibrarySets,
} from "client/store"
import {
    PrepareNestedHistory,
    NestedHistory,
    TableGroupedIds,
} from "client/components"

import pdfmake from "client/components/pdfmake/pdfmake"
import vfs from "client/components/pdfmake/vfs_fonts"
import fontList from "client/components/pdfmake/fontList"

import {
    ReportAccountLogo,
    ReportProjectLogo,
} from "client/components/pdfmake/template/functions"
import * as Const from "client/components/pdfmake/template/const"
import templateStyles from "client/components/pdfmake/template/styles"

import { Header, Footer } from "./headerFooter"
import TitlePage from "./titlePage"
import CopyrightPage from "./copyrightPage"
import OverviewPage from "./overviewPage"
import ComplianceSetsPage from "./complianceSetsPage"


pdfmake.addVirtualFileSystem(vfs)
pdfmake.addFonts(fontList)

const ComplianceExportToPDF = (
    revisionActiveList: ComplianceRevisionActiveList | null,
    project: MtProject,
    moduleList: ModuleList,
    userList: UserList,
    userSettings: UserSettings,
    userData: UserData,
    lang: LangType,
    callback: (isLoading: boolean) => void,
) => {
    callback(true)

    Promise.all([
        // compliance approval levels
        LoadCcApprovalLevels(userData.accountId, project.id, 1)
        .then((res) => res),

        // responsible responses
        LoadCcResponses(userData.accountId, project.id, 0)
        .then((res) => res.responseList),
        // compliance responses
        LoadCcResponses(userData.accountId, project.id, 1)
        .then((res) => res.responseList),

        LoadAllCcLibrarySets(userData.accountId, project.id)
        .then((setsResponse) => {
            const setList: CcLibrarySetList = {}
            let itemList: CcLibraryItemList = {}
            let nested: { [setId: number]: TableGroupedIds } = {}

            const revisionList: ComplianceRevisionActiveList = {}
            let complianceStateList: ComplianceStateList = {}
            let complianceHistoryList: ComplianceHistoryList = {}
            const complianceHistoryNestedList: { [itemId: number]: NestedHistory[] } = {}
            const promiseArray: Promise<any>[] = []

            Object.values(setsResponse).forEach((set: CcLibrarySet) => {
                set.modules.forEach((row) => {
                    if (row.module === COMPLIANCE) {
                        setList[set.id] = set

                        promiseArray.push(
                            LoadNestedItems(set.id).then((res) => {
                                itemList = { ...itemList, ...res.itemList }
                                nested = {
                                    ...nested,
                                    [set.id]: res.nested,
                                }
                            }),
                            LoadCcComplianceRevisions(set.id).then((revisions) => {
                                let revision = revisions[revisions.length - 1] // by default use the last revision
                                if (revisionActiveList && revisionActiveList[set.id]) revision = revisionActiveList[set.id]

                                revisionList[set.id] = revision

                                return Promise.all([
                                    LoadAllCcComplianceItemState(revision.id).then((res) => {
                                        complianceStateList = {
                                            ...complianceStateList,
                                            ...res,
                                        }
                                    }),
                                    LoadAllCcComplianceHistory(revision.id).then((res) => {
                                        complianceHistoryList = {
                                            ...complianceHistoryList,
                                            ...res,
                                        }
                                        Object.keys(res).forEach((index) => {
                                            const itemId = Number(index)
                                            complianceHistoryNestedList[itemId] = PrepareNestedHistory(complianceHistoryList[itemId])
                                        })
                                    }),
                                ])
                            }),
                        )
                    }
                })
            })

            const sortedIds = SortCcLibrarySets(Object.values(setList), "name", ASC, {})

            return Promise.all(promiseArray).then(() => ({
                setList,
                setIds: sortedIds,
                itemList,
                nested,
                revisionList,
                complianceStateList,
                complianceHistoryList,
                complianceHistoryNestedList,
            }))
        }),
    ]).then(([
        complianceLevels,
        responsibleResponses,
        complianceResponses,
        setData,
    ]) => {
        let accountLogo: string
        let projectLogo: string

        // load all used images as base64 before creating pdf
        const promiseArray: Promise<any>[] = [
            ReportAccountLogo(userData).then((res) => {
                accountLogo = res
            }),
            ReportProjectLogo(project, userSettings).then((res) => {
                projectLogo = res
            }),
        ]

        // load compliance set logos as base64
        const setImageBase64: { [setId: number]: string } = {}
        const defaultSetLogo = `${window.location.origin}${Images.complianceSetLogo}`

        setData.setIds.forEach((setId) => {
            const set = setData.setList[setId]
            promiseArray.push(
                GetFile(set.logo, defaultSetLogo).then((res) => {
                    setImageBase64[set.id] = res.data
                }),
            )
        })


        Promise.all(promiseArray).then(() => {
            const responseList = {
                ...responsibleResponses,
                ...complianceResponses,
            }

            const docDefinition = {
                info: {
                    title: lang.ccComplianceReport,
                    author: lang.risknowlogy,
                    subject: lang.result,
                    keywords: "CC, PDF, Compliance, Risknowlogy",
                },
                pageMargins: Const.PAGE_MARGINS,
                pageSize: "A4",
                images: {
                    accountLogo,
                    projectLogo,
                    downwardArrow: `${window.location.origin}${Images.downwardArrow}`,
                },
                defaultStyle: {
                    fontSize: 11,
                    font: "Arial",
                },
                styles: templateStyles,
                header: (currentPage: number) => Header(currentPage, project),
                footer: (currentPage: number, pageCount: number) => (
                    Footer(currentPage, pageCount, project, userData, lang)
                ),
                content: TitlePage(project, lang).concat(
                    CopyrightPage(moduleList, lang),
                    [{
                        pageBreak: "before",
                        toc: {
                            title: {
                                text: lang.tableOfContents,
                                style: Const.HEADER,
                                tocItem: true,
                                tocStyle: Const.TOC_HEADER,
                                tocMargin: [0, 5, 0, 0],
                            },
                            numberStyle: {
                                bold: true,
                                fontSize: 11,
                            },
                        },
                    }],
                    OverviewPage(setData.setList, setData.setIds, project, lang),
                    ComplianceSetsPage(
                        setData.setList,
                        setData.setIds,
                        setData.itemList,
                        setData.nested,
                        setData.revisionList,
                        setData.complianceStateList,
                        setData.complianceHistoryList,
                        setData.complianceHistoryNestedList,

                        setImageBase64,

                        complianceLevels.levelList,
                        complianceLevels.levelIds,
                        responseList,
                        userList,
                        lang,
                    ),
                ),
            }

            callback(false)
            pdfmake.createPdf(docDefinition).open()
        })
    })
}
export default ComplianceExportToPDF
